import { useCallback, useEffect, useRef, useState } from "react"

import { CAST_APP_ID, isPresentationSenderSupported, presentationUrl } from "./castConfig"
import type { PresentationConnection, PresentationRequest } from "./presentationTypes"
import type { CastState } from "./useCastSender"

/**
 * Casting via the W3C Presentation API.
 *
 * Same job as the Cast sender next door — put /tv on a television and tell it
 * which room — but through the standard rather than Google's SDK, which buys
 * three things the Cast session model doesn't have:
 *
 *  - **The board outlives its controller.** The spec is explicit that a
 *    presentation "continues to run even after the original page that started
 *    the presentation closes its PresentationConnection, navigates, or is
 *    closed". Locking the phone, refreshing the lobby or losing the tab leaves
 *    the television playing; with a Cast session all three take it down.
 *  - **Reconnect.** `reconnect(id)` resumes control of a presentation already
 *    running, so a returning phone picks the TV back up instead of launching a
 *    second one.
 *  - **The browser's own cast button.** Setting `navigator.presentation
 *    .defaultRequest` means Chrome's built-in affordance offers this
 *    presentation, so people can reach it where they already look.
 *
 * What it does not buy is reach: no engine outside Chromium implements this, so
 * an iPhone is no better served here than by Cast. Availability decides which
 * transport the button uses; see CastButton.
 */

/** Where we remember a running presentation, so a reload can rejoin it. Session
 *  rather than local storage: a presentation doesn't outlive the browser. */
const ID_KEY = "mq:presentation"

function rememberId(roomCode: string, id: string) {
    try { sessionStorage.setItem(ID_KEY, JSON.stringify({ roomCode, id })) } catch { /* private mode */ }
}

function recallId(roomCode: string): string | null {
    try {
        const raw = sessionStorage.getItem(ID_KEY)
        if (!raw) return null
        const saved = JSON.parse(raw) as { roomCode?: string; id?: string }
        // A remembered presentation for a different room is somebody else's
        // game now — reconnecting to it would show the wrong board.
        return saved.roomCode === roomCode && saved.id ? saved.id : null
    } catch {
        return null
    }
}

function forgetId() {
    try { sessionStorage.removeItem(ID_KEY) } catch { /* ignore */ }
}

export function usePresentationSender(roomCode: string | null) {
    const [state, setState] = useState<CastState>({ kind: "unavailable" })
    const requestRef = useRef<PresentationRequest | null>(null)
    const connectionRef = useRef<PresentationConnection | null>(null)

    /** Wire a live connection into component state and keep the room in sync. */
    const adopt = useCallback((connection: PresentationConnection, code: string) => {
        connectionRef.current = connection
        rememberId(code, connection.id)

        const onConnected = () => {
            setState({ kind: "connected", deviceName: null })
            // The URL already carries the room; this covers a presentation that
            // was reconnected to, or launched before the code was known.
            try { connection.send(JSON.stringify({ roomCode: code })) } catch { /* not open yet */ }
        }

        connection.onconnect = onConnected
        connection.onclose = () => {
            // Detached, not ended — per spec the television carries on. We drop
            // to idle so the button offers to reconnect rather than relaunch.
            connectionRef.current = null
            setState({ kind: "idle" })
        }
        connection.onterminate = () => {
            connectionRef.current = null
            forgetId()
            setState({ kind: "idle" })
        }

        if (connection.state === "connected") onConnected()
        else setState({ kind: "connecting" })
    }, [])

    /* ── Build the request, watch availability, resume anything running ──── */
    useEffect(() => {
        if (!roomCode || !isPresentationSenderSupported()) {
            setState({ kind: "unavailable" })
            return
        }

        let cancelled = false
        let request: PresentationRequest
        try {
            /*
             * Two URLs, most-wanted first — the spec lets a request carry a list
             * and the user agent picks the first it can honour.
             *
             * `cast:<id>` names a registered Cast receiver, which a Chromecast
             * launches natively (2-UA): the device loads the page itself. The
             * https URL is the fallback, and what an unregistered build gets —
             * but Chrome serves that by rendering the page here and mirroring it
             * as video (1-UA), which is why an unregistered cast looks soft and
             * runs a beat behind. Offering both means the good path is taken
             * wherever it exists without giving up the one that always works.
             */
            const urls = [presentationUrl(window.location.origin, roomCode)]
            if (CAST_APP_ID) urls.unshift(`cast:${CAST_APP_ID}`)
            request = new window.PresentationRequest!(urls)
        } catch {
            // Thrown for an insecure context, among others. Nothing to offer.
            setState({ kind: "unavailable" })
            return
        }
        requestRef.current = request
        // Offer the button straight away. See the availability note below.
        setState(prev => (prev.kind === "unavailable" ? { kind: "idle" } : prev))

        // Chrome's own cast affordance now launches our board rather than
        // offering to mirror the tab. The spec still requires a user gesture to
        // actually start one, so this only changes what that gesture does.
        const presentation = navigator.presentation
        const previousDefault = presentation?.defaultRequest ?? null
        if (presentation) presentation.defaultRequest = request

        // A presentation started elsewhere (the browser's button) arrives here.
        request.onconnectionavailable = event => {
            if (!cancelled) adopt(event.connection, roomCode)
        }

        /*
         * Availability is watched but never used to hide the button.
         *
         * Chrome doesn't scan the network until the user shows cast intent, so
         * `value` sits false until they've opened the browser's own cast menu
         * once — which meant our button only appeared *after* you'd already
         * found the thing it exists to save you from. It's a hint about what is
         * known so far, not a statement that no television exists.
         *
         * So the button shows wherever the API works, and an empty picker is
         * Chrome's to explain — exactly as its own cast button behaves.
         */
        request
            .getAvailability()
            .then(availability => {
                if (cancelled) return
                const sync = () => {
                    if (availability.value) {
                        setState(prev => (prev.kind === "unavailable" ? { kind: "idle" } : prev))
                    }
                }
                availability.onchange = sync
                sync()
            })
            .catch(() => { /* Can't monitor; the picker still works. */ })

        // Resume a board this phone already put on a screen.
        const existing = recallId(roomCode)
        if (existing) {
            request.reconnect(existing)
                .then(connection => { if (!cancelled) adopt(connection, roomCode) })
                .catch(() => forgetId())
        }

        return () => {
            cancelled = true
            // Leave the presentation running — that is the whole point of this
            // transport — but stop steering the browser's button at a request
            // belonging to a room we've left.
            if (presentation && presentation.defaultRequest === request) {
                presentation.defaultRequest = previousDefault
            }
        }
    }, [roomCode, adopt])

    const startCasting = useCallback(() => {
        const request = requestRef.current
        if (!request || !roomCode) return
        // Must be called straight from a click: the picker needs a user gesture.
        request.start()
            .then(connection => adopt(connection, roomCode))
            .catch(error => {
                // NotAllowedError is the user dismissing the picker.
                if ((error as DOMException)?.name !== "NotAllowedError") {
                    console.warn("Presentation: could not start", error)
                }
            })
    }, [roomCode, adopt])

    const stopCasting = useCallback(() => {
        // `terminate`, not `close`: the button says stop, and closing would only
        // detach this phone while the television carried on showing the game.
        connectionRef.current?.terminate()
        connectionRef.current = null
        forgetId()
        setState({ kind: "idle" })
    }, [])

    return { state, startCasting, stopCasting }
}
